import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getEventById } from "../../ApiService/a-api.service";
import { Types } from "../../../types/types";
import ModalNoAuth from "../../ui/ModalNoAuth/ModalNoAuth";
import { Modal } from "../../ui/Modal/Modal";
import "./MoviePage.component.scss";
import ChevronUp from "../../../icons/chevrons-up.svg";
import ChevronDown from "../../../icons/chevrons-down.svg";
import CalendarIcon from "../../../icons/Calendar.svg";
import PinIcon from "../../../icons/pin.svg";
import RadioButtton from "..//..//RadioButtton/RadioButtton";
import OrganizerTooltip from "./OrganizerTooltip";
import ArrowRight from "../../../icons/arrowRight.svg";
import Check from "../../../icons/Vector@2x.png";
import closeBtn from "../../../icons/closeIcon.svg";
import modalConvertIcon from "../../../icons/convertIcon.svg";
import BuyTickets from "../../ui/BuyTickets/BuyTickets";
import { API_BASE } from "../../../config/api";
import { getAuthToken } from "utils/cookies";

// --------------------- Интерфейсы и типы ---------------------
interface Photo {
  id: number;
  medium: string;
  sorter: number;
}

type ExtendedEvent = Types & {
  gallery?: string[];
};

type SelectedTicketSet = { ticket_type: number; quantity: number }[];

type EventDateView =
  | {
      type: "single-day";
      date: string;
      time: string;
    }
  | {
      type: "multi-day";
      startDate: string;
      startTime: string;
      endDate: string;
      endTime: string;
    };

function getEventDateView(
  startISO?: string,
  endISO?: string
): EventDateView | null {
  if (!startISO || !endISO) return null;

  const start = new Date(startISO);
  const end = new Date(endISO);

  const dateOptions: Intl.DateTimeFormatOptions = {
    weekday: "long",
    day: "numeric",
    month: "long",
  };
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
  };

  const startDate = start.toLocaleDateString("ru-RU", dateOptions);
  const endDate = end.toLocaleDateString("ru-RU", dateOptions);
  const startTime = start.toLocaleTimeString("ru-RU", timeOptions);
  const endTime = end.toLocaleTimeString("ru-RU", timeOptions);

  const isSameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();

  if (isSameDay) {
    // вариант 1: один день
    return {
      type: "single-day",
      date: startDate,
      time: `${startTime}–${endTime}`,
    };
  }

  // вариант 2: разные дни
  return {
    type: "multi-day",
    startDate,
    startTime,
    endDate,
    endTime,
  };
}

// --------------------- Основной компонент ---------------------
function MoviePage() {
  const [mainPhotoIndex, setMainPhotoIndex] = useState<number>(0);
  const { id } = useParams();
  const [event, setEvent] = useState<ExtendedEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  // Флаг раскрытия "Все билеты"
  const [isOpen, setIsOpen] = useState(false);

  // Проверка куки-авторизации (простая логика)
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);

  // Модалки
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  //мейл организатора
  const [userEmail, setUserEmail] = useState<string>("");

  //отслеживаем ввод инпута
  const [contactMessage, setContactMessage] = useState("");
  const [contactError, setContactError] = useState<string | null>(null);

  const [reportReason, setReportReason] = useState<
    "fraud" | "insult_and_rudeness" | "other" | null
  >(null);
  const [reportMessage, setReportMessage] = useState("");

  // Подсказка (tooltip) при наведении на организатора
  const [showTooltip, setShowTooltip] = useState(false);
  const linkRef = useRef<HTMLAnchorElement | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  // Выбранные данные для покупки
  const [selectedTickets, setSelectedTickets] = useState<SelectedTicketSet>([]);

  // --------------------- useEffect: проверка авторизации ---------------------
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const authToken = getAuthToken();
        const userId = localStorage.getItem("userId");
        const response = await fetch(`${API_BASE}/users/profile/${userId}`, {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          credentials: "include",
        });
        if (response.ok) {
          const userData = await response.json();
          setUserEmail(userData.email); // Предполагая, что API возвращает поле email
          setIsUserLoggedIn(true);
        } else {
          setIsUserLoggedIn(false);
        }
      } catch (error) {
        console.error("Проверка авторизации не удалась:", error);
        setIsUserLoggedIn(false);
      }
    };

    checkAuth();
  }, []);

  // --------------------- useEffect: загрузка события ---------------------
  useEffect(() => {
    if (!id) {
      setError("Некорректный ID события");
      setLoading(false);
      return;
    }
    getEventById(id)
      .then((data) => {
        if (!data) throw new Error("Событие не найдено");

        const finalEvent: ExtendedEvent = {
          ...data,
        };

        setEvent(finalEvent);

        const soldOut = !finalEvent.ticket_types_data?.some(
          (t) => (t?.max_count ?? 0) > 0
        );

        if (!soldOut && finalEvent.ticket_types_data?.length) {
          const first = finalEvent.ticket_types_data[0].id;
          setSelectedTickets([{ ticket_type: first, quantity: 1 }]);
        } else {
          setSelectedTickets([]);
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  // --------------------- Хэндлеры ---------------------

  // Contact
  const openContactModal = () => {
    setContactMessage("");
    setContactError(null);
    setIsContactModalOpen(true);
  };
  const closeContactModal = () => {
    setIsContactModalOpen(false);
    setContactMessage("");
    setContactError(null);
  };

  // Report
  const openReportModal = () => {
    setReportMessage("");
    setReportReason(null);
    setIsReportModalOpen(true);
  };
  const closeReportModal = () => {
    setIsReportModalOpen(false);
    setReportMessage("");
    setReportReason(null);
  };

  const handleMouseEnter = () => {
    if (linkRef.current) {
      const rect = linkRef.current.getBoundingClientRect();
      setTooltipPosition({ top: rect.top - 90, left: rect.left });
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const reasonTextMap: Record<
    "fraud" | "insult_and_rudeness" | "other",
    string
  > = {
    fraud: "Мошенничество",
    insult_and_rudeness: "Оскорбления и хамство",
    other: "Другая причина",
  };

  const handleSendReport = async (payload: {
    reason: "fraud" | "insult_and_rudeness" | "other";
    message: string;
  }) => {
    try {
      const token = getAuthToken();
      if (!token) throw new Error("Вы не авторизованы");

      const res = await fetch(`${API_BASE}/complaints/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_to: event?.owner_info?.id ?? null,
          type_complaint: payload.reason,
          description: payload.message,
        }),
      });

      if (!res.ok) throw new Error("Ошибка отправки жалобы");

      alert("Жалоба отправлена");
      closeReportModal();
    } catch (e) {
      console.error(e);
      alert("Не удалось отправить жалобу. Попробуйте позже.");
    }
  };

  const handleReportClick = () => {
    if (!reportReason) return;

    const descriptionToSend =
      reportMessage.trim().length > 0
        ? reportMessage.trim()
        : reasonTextMap[reportReason];

    handleSendReport({
      reason: reportReason,
      message: descriptionToSend,
    });
  };

  // Изменённый обработчик выбора билета
  const handleTicketSelect = (ticketId: number) => {
    const selectedTicketData = event?.ticket_types_data?.find(
      (t) => t.id === ticketId
    );
    if (selectedTicketData?.max_count === 0) {
      alert("Извините, все билеты этой категории распроданы");
      return;
    }

    setSelectedTickets((prev) => {
      const exists = prev.find((t) => t.ticket_type === ticketId);
      if (exists) {
        // Удаляем билет из списка
        return prev.filter((t) => t.ticket_type !== ticketId);
      } else {
        // Добавляем билет с количеством 1
        return [...prev, { ticket_type: ticketId, quantity: 1 }];
      }
    });
  };

  // Изменённый обработчик изменения количества
  const handleQuantityChange = (ticketId: number, quantity: number) => {
    setSelectedTickets((prev) =>
      prev.map((t) =>
        t.ticket_type === ticketId
          ? { ...t, quantity: Math.max(1, Math.min(quantity, 10)) }
          : t
      )
    );
  };

  //отправка сообщения мод/окна от пользователя организатору
  const handleSendMessage = async () => {
    if (contactMessage.length < 10) {
      setContactError("Сообщение должно содержать минимум 10 символов");
      return;
    }
    if (contactMessage.length > 2000) {
      setContactError("Сообщение не может превышать 2000 символов");
      return;
    }

    try {
      const authToken = getAuthToken();
      if (!authToken) throw new Error("Требуется авторизация");

      await fetch(`${API_BASE}/mailing/send-message/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          event_id: id,
          text: contactMessage,
        }),
      });

      setContactError(null);
      setContactMessage("");
      closeContactModal();
    } catch {
      setContactError("Ошибка отправки сообщения. Попробуйте ещё раз.");
    }
  };

  // Покупка билетов
  const handleInfoTicket = async () => {
    if (isSoldOut) return;
    if (!isUserLoggedIn) {
      openContactModal();
      return;
    }
    setIsAuthModalOpen(true);
  };

  const handleBuyTicket = async (promoCode: string | null) => {
    if (!isUserLoggedIn) {
      openContactModal();
      return;
    }
    try {
      const authToken = getAuthToken();
      const orderResponse = await fetch(`${API_BASE}/tickets/order/create/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        credentials: "include",
        body: JSON.stringify({
          ticketset: selectedTickets,
          promo_code: promoCode,
        }),
      });

      if (!orderResponse.ok) {
        throw new Error(
          `Ошибка при создании заказа (статус: ${orderResponse.status})`
        );
      }

      const orderResult = await orderResponse.json();
      const paymentInitResponse = await fetch(
        `${API_BASE}/tickets/orders/init-payment/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          credentials: "include",
          body: JSON.stringify({
            order_id: orderResult.id,
          }),
        }
      );

      if (!paymentInitResponse.ok) {
        throw new Error(
          `Ошибка при инициализации платежа (статус: ${paymentInitResponse.status})`
        );
      }

      const paymentResult = await paymentInitResponse.json();

      if (paymentResult.confirmation_url) {
        window.location.href = paymentResult.confirmation_url;
      } else {
        throw new Error("Не получен URL для оплаты");
      }
    } catch (err: any) {
      console.error("Ошибка при покупке билета:", err);
      alert(err.message || "Произошла ошибка при покупке билета");
    }
  };

  // Проверка, распроданы ли все билеты
  const isSoldOut = !event?.ticket_types_data?.some(
    (t) => (t?.max_count ?? 0) > 0
  );

  const isReportValid =
    !!reportReason &&
    (reportReason !== "other" || reportMessage.trim().length >= 10);

  const reportError =
    reportReason === "other" && reportMessage.trim().length < 10;

  // --------------------- Рендер ---------------------
  if (loading) return <p>Загрузка...</p>;
  if (error === "Событие не найдено") navigate("/404");
  if (error) return <p>Ошибка: {error}</p>;
  if (event?.is_owner) navigate(`/organizer_event/${id}`); // Если организатор, перенаправляем на страницу события

  const organizer = {
    name: event?.owner_info?.first_name || "Организатор",
    imageUrl:
      event?.owner_info?.avatar_small || "https://via.placeholder.com/100",
    description: "Провёл более 100 концертов",
  };

  const dateView = getEventDateView(event?.date_start, event?.date_end);

  function getCorrectPluralForm(count: number): string {
    const lastDigit = count % 10;
    const lastTwoDigits = count % 100;

    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
      return "идут";
    }

    if (lastDigit === 1) {
      return "идет";
    }

    if (lastDigit >= 2 && lastDigit <= 4) {
      return "идут";
    }

    return "идут";
  }

  const mainCategory = event?.category?.[0];

  const capitalizeFirst = (str: string) => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  return (
    <div>
      <div className="movie-page">
        <div className="content-columns">
          <div className="left-column">
            <div className="event-image-block">
              <img
                className="event-image"
                src={event?.imageUrl}
                alt={event?.name}
              />
            </div>
            <div className="categories">
              {event?.category?.map((cat, index) => (
                <Link to="#" className="category" key={index}>
                  #{cat.name}
                </Link>
              ))}
            </div>
            <div className="event-organizer">
              <h2 className="org">Организатор</h2>
              <div className="organizer-info">
                <img
                  className="organizer-avatar"
                  src={event?.owner_info?.avatar_small}
                  alt={event?.owner_info?.first_name}
                />
                <Link
                  to={`/organizer_page/${event?.owner_info?.id}`}
                  className="organaizer-Name"
                  ref={linkRef}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  {event?.owner_info?.first_name}
                </Link>
                <img src={ArrowRight} alt="ArrowRight" />
              </div>
              {showTooltip && (
                <OrganizerTooltip
                  organizer={organizer}
                  position={tooltipPosition}
                />
              )}

              <button className="organizer-button" onClick={openContactModal}>
                Связаться с организатором
              </button>
              <button className="organizer-button" onClick={openReportModal}>
                Пожаловаться на событие
              </button>

              {event?.visitors?.total ? (
                <>
                  {/* Показываем, сколько людей купило билет */}
                  <div
                    className="organizer-button organizer-people"
                    style={{ textDecoration: "none" }}
                  >
                    {event.visitors.total}{" "}
                    {getCorrectPluralForm(event.visitors.total)}
                  </div>

                  <div className="all-person" aria-hidden="false">
                    {event?.visitors?.list &&
                      event.visitors.list.length > 0 &&
                      (() => {
                        const visitors = event.visitors.list;
                        const maxVisible = 5;
                        const visible = visitors.slice(0, maxVisible);

                        return (
                          <div className="avatar-stack" aria-hidden="true">
                            {visible.map((visitor, idx) => (
                              <div
                                key={visitor.id ?? idx}
                                className="avatar-item"
                                style={{ zIndex: 100 + idx }}
                                aria-hidden="true"
                              >
                                <img
                                  src={visitor.avatar_small}
                                  alt=""
                                  draggable={false}
                                  className="avatar-item__img"
                                />
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                  </div>

                  <p className="pis">
                    {event.visitors.list && event.visitors.list.length > 0 && (
                      <>
                        {event.visitors.list
                          .slice(0, 3)
                          .map((v) => v.first_name)
                          .join(", ")}
                        {event.visitors.total > event.visitors.list.length &&
                          ` и ещё ${
                            event.visitors.total - event.visitors.list.length
                          }`}
                      </>
                    )}
                  </p>
                </>
              ) : null}
            </div>
          </div>

          {/* Основная информация */}
          <div className="right-column">
            <div className="event-header">
              <nav className="event-category" aria-label="Хлебные крошки">
                <ul className="event-category__list">
                  <li className="event-category__item">
                    <Link to="/" className="event-category__link">
                      События
                    </Link>
                    <img
                      src={ArrowRight}
                      alt=""
                      aria-hidden="true"
                      className="event-category__icon"
                    />
                  </li>

                  {mainCategory && (
                    <li className="event-category__item">
                      <Link to="/" className="event-category__link">
                        {mainCategory.name}
                      </Link>
                      <img
                        src={ArrowRight}
                        alt=""
                        aria-hidden="true"
                        className="event-category__icon"
                      />
                    </li>
                  )}

                  <li
                    className="event-category__item event-category__item--current"
                    aria-current="page"
                  >
                    {event?.name}
                  </li>
                </ul>
              </nav>
              <div className="event-info">
                <h1 className="event-title">{event?.name}</h1>

                <div className="date-time">
                  <div className="icons">
                    <img
                      src={CalendarIcon}
                      alt=""
                      className="icon"
                      aria-hidden="true"
                    />
                  </div>
                  <div>
                    {dateView && dateView.type === "single-day" && (
                      <>
                        <p className="event-date">
                          {capitalizeFirst(dateView.date)}
                        </p>
                        <p className="event-time">{dateView.time}</p>
                      </>
                    )}

                    {dateView && dateView.type === "multi-day" && (
                      <>
                        <p className="event-date">
                          {capitalizeFirst(dateView.startDate)}{" "}
                          <span className="event-time-inline">
                            {dateView.startTime}
                          </span>
                        </p>
                        <p className="event-date">
                          {capitalizeFirst(dateView.endDate)}{" "}
                          <span className="event-time-inline">
                            {dateView.endTime}
                          </span>
                        </p>
                      </>
                    )}
                  </div>
                </div>

                <div className="location-street">
                  <div className="icons">
                    <img
                      src={PinIcon}
                      alt=""
                      className="icon"
                      aria-hidden="true"
                    />
                  </div>
                  <address className="event-address">
                    <p className="event-location">{event?.place_city}</p>
                    <p className="event-street">{event?.place}</p>
                  </address>
                </div>
              </div>

              <div className="event-price-box">
                <p className="event-price">{event?.price}</p>
              </div>
            </div>

            {/* Кнопка "Все билеты" и выпадающий список */}
            <div className="event-tickets">
              <button
                className={`full-width-button ${
                  isOpen ? "full-width-button--open" : ""
                }`}
                onClick={() => setIsOpen(!isOpen)}
              >
                <span className="full-width-button-text">
                  Все билеты / {event?.name}
                </span>
                <img
                  src={isOpen ? ChevronUp : ChevronDown}
                  alt="toggle"
                  className="dropdown-icon"
                />
              </button>

              {isOpen && (
                <div className="tickets-container">
                  {isSoldOut ? (
                    <div className="tickets-soldout">
                      <p className="tickets-soldout-title">Распродано</p>
                      <p className="tickets-soldout-subtitle">
                        Билеты на это событие распроданы.
                      </p>
                    </div>
                  ) : (
                    <>
                      <p className="tickets-instruction">
                        Пожалуйста, выберите желаемый тип билета:
                      </p>
                      {event?.ticket_types_data &&
                        Array.isArray(event.ticket_types_data) &&
                        event.ticket_types_data.map((ticket, index) => {
                          const selected = selectedTickets.find(
                            (t) => t.ticket_type === ticket.id
                          );
                          return (
                            <div
                              className={`ticket-card ${
                                selected ? "selected" : ""
                              } ${ticket.max_count === 0 ? "sold-out" : ""}`}
                              key={index}
                              onClick={() => {
                                if (ticket.max_count !== 0) {
                                  handleTicketSelect(ticket.id);
                                }
                              }}
                            >
                              <div className="ticket-info">
                                <div className="ticket-check-container">
                                  <div
                                    className={`ticket-check-bg ${
                                      selected ? "selected" : ""
                                    }`}
                                  >
                                    <img
                                      src={Check}
                                      className="ticket-check"
                                      alt="Выбрано"
                                    />
                                  </div>
                                </div>
                                <div className="ticket-info-container">
                                  <h3 className="ticket-type">{ticket.name}</h3>
                                  <p className="ticket-price">
                                    {ticket.price
                                      ? `${ticket.price.toLocaleString()} ₽`
                                      : "Бесплатно"}
                                  </p>
                                  <div className="ticket-count">
                                    <p>Осталось {ticket.max_count}</p>
                                  </div>
                                </div>
                              </div>
                              <div className="ticket-controls">
                                {selected && ticket.max_count > 0 && (
                                  <div className="quantity-selector">
                                    <button
                                      className="quantity-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleQuantityChange(
                                          ticket.id,
                                          (selected.quantity || 1) - 1
                                        );
                                      }}
                                    >
                                      -
                                    </button>
                                    <input
                                      type="number"
                                      min="1"
                                      max={Math.min(ticket.max_count, 10)}
                                      value={selected.quantity}
                                      className="quantity-input"
                                      onChange={(e) => {
                                        const value = parseInt(e.target.value);
                                        if (!isNaN(value)) {
                                          handleQuantityChange(
                                            ticket.id,
                                            Math.max(1, Math.min(value, 10))
                                          );
                                        }
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                    <button
                                      className="quantity-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (
                                          selected.quantity <
                                          Math.min(ticket.max_count, 10)
                                        ) {
                                          handleQuantityChange(
                                            ticket.id,
                                            selected.quantity + 1
                                          );
                                        }
                                      }}
                                      disabled={
                                        selected.quantity >=
                                        Math.min(ticket.max_count, 10)
                                      }
                                    >
                                      +
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      <button
                        className={`buy-button ${
                          event?.is_finished || selectedTickets.length === 0
                            ? "disabled-button"
                            : ""
                        }`}
                        onClick={handleInfoTicket}
                        disabled={
                          event?.is_finished || selectedTickets.length === 0
                        }
                      >
                        {event?.is_finished
                          ? "Событие завершено"
                          : "Купить билет"}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Описание события и галерея */}
            <div className="gallery-container">
              <div className="event-description">
                <h2 className="event-about">О событии</h2>
                <p className="event-about-text">
                  {event?.description || "Описание временно отсутствует"}
                </p>
              </div>
              <div className="event-gallery">
                {event?.photos_data &&
                  Array.isArray(event.photos_data) &&
                  event.photos_data.length > 0 && (
                    <>
                      <img
                        src={event.photos_data[mainPhotoIndex].medium}
                        alt="Main Event"
                        className="gallery-main-image"
                      />
                      <div className="gallery-thumbnails">
                        {event.photos_data
                          .map((photo: Photo, index: number) => ({
                            photo,
                            index,
                          }))
                          .filter(({ index }) => index !== mainPhotoIndex)
                          .slice(0, 4)
                          .map(({ photo, index }) => (
                            <img
                              key={index}
                              src={photo.medium}
                              alt="Event thumbnail"
                              className="gallery-thumbnail"
                              onClick={() => setMainPhotoIndex(index)}
                            />
                          ))}
                      </div>
                    </>
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Модальное окно "Связаться с организатором" */}
      <Modal isOpen={isContactModalOpen} onClose={closeContactModal}>
        <div className="modal contact-form">
          <div className="modalCloseBtn">
            <button type="button" onClick={closeContactModal}>
              <img src={closeBtn} alt="Закрыть" />
            </button>
          </div>
          {/* условный рендер контента мод/окна - с проверкой авторизован ли пользователь или нет */}
          {isUserLoggedIn ? (
            <div className="modalForAuth">
              <div className="modalIcon">
                <img
                  className="modalConvertIcon"
                  src={modalConvertIcon}
                  alt="convertIcon"
                />
              </div>

              <div className="modalContent">
                <h4 className="modalTitle">Связаться с организатором</h4>
                <p className="modalsubTitle">
                  Если есть вопрос о событии? Вы можете отправить сообщение
                  организатору
                </p>

                <textarea
                  placeholder="Какой у вас вопрос к организатору?"
                  value={contactMessage}
                  onChange={(e) => {
                    const v = e.target.value;
                    setContactMessage(v);
                    if (v.length < 10)
                      setContactError(
                        "Сообщение должно содержать минимум 10 символов"
                      );
                    else if (v.length > 100)
                      setContactError(
                        "Сообщение не может превышать 100 символов"
                      );
                    else setContactError(null);
                  }}
                  className={`bigTextarea ${contactError ? "input-error" : ""}`}
                />

                <p className="modalSymbolInfo">Не более 100 символов</p>

                <p className="modalNotes">
                  Организатор отправит сообщение на {userEmail}.
                </p>
              </div>

              <button
                className={`sendButton big ${
                  contactMessage && !contactError ? "active" : ""
                }`}
                onClick={handleSendMessage}
                disabled={
                  !contactMessage ||
                  contactMessage.trim().length < 10 ||
                  contactMessage.trim().length > 100
                }
              >
                Отправить сообщение
              </button>
            </div>
          ) : (
            <ModalNoAuth />
          )}
        </div>
      </Modal>

      {/* Модальное окно "Покупки билетов" */}
      <Modal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        contentClassName="modal-content_buy-tickets"
      >
        <BuyTickets
          event={event}
          selectedTickets={selectedTickets}
          onBuyTicket={handleBuyTicket}
        />
      </Modal>

      {/* Модальное окно "Пожаловаться" */}
      <Modal isOpen={isReportModalOpen} onClose={closeReportModal}>
        <div className="modal">
          <div className="modalCloseBtn">
            <button type="button" onClick={closeReportModal}>
              <img src={closeBtn} alt="Закрыть" />
            </button>
          </div>

          {isUserLoggedIn ? (
            <div className="modalForAuth">
              <div className="modalContent">
                <h4 className="modalTitle">Расскажите, что не так</h4>

                <div className="radioGroup">
                  <label className="radioLabel">
                    <RadioButtton
                      className="radiobtn"
                      checked={reportReason === "fraud"}
                      onChange={() => setReportReason("fraud")}
                      size={24}
                      strokeColor="#262429"
                      fillColor={
                        reportReason === "fraud" ? "#262429" : "transparent"
                      }
                      uncheckedColor="#c0bbc7"
                    />
                    Мошенничество
                  </label>

                  <label className="radioLabel">
                    <RadioButtton
                      className="radiobtn"
                      checked={reportReason === "insult_and_rudeness"}
                      onChange={() => setReportReason("insult_and_rudeness")}
                      size={24}
                      strokeColor="#262429"
                      fillColor={
                        reportReason === "insult_and_rudeness"
                          ? "#262429"
                          : "transparent"
                      }
                      uncheckedColor="#c0bbc7"
                    />
                    Оскорбления и хамство
                  </label>

                  <label className="radioLabel">
                    <RadioButtton
                      className="radiobtn"
                      checked={reportReason === "other"}
                      onChange={() => setReportReason("other")}
                      size={24}
                      strokeColor="#262429"
                      fillColor={
                        reportReason === "other" ? "#262429" : "transparent"
                      }
                      uncheckedColor="#c0bbc7"
                    />
                    Другая причина
                  </label>
                </div>

                <textarea
                  value={reportMessage}
                  onChange={(e) => setReportMessage(e.target.value)}
                  className={`bigTextarea ${reportError ? "input-error" : ""}`}
                  maxLength={2000}
                  placeholder="Напишите подробнее о том, что у вас случилось"
                />
              </div>

              <button
                className={`sendButton big reportButton ${
                  isReportValid ? "active" : ""
                }`}
                disabled={!isReportValid}
                onClick={handleReportClick}
              >
                Пожаловаться
              </button>
            </div>
          ) : (
            <ModalNoAuth />
          )}
        </div>
      </Modal>
    </div>
  );
}

export default MoviePage;
